import React, { useState } from 'react';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';

import { isEmpty } from 'dpl/shared/utils/object';
import { CONSTRAINT_TYPES } from 'dpl/shared/validations/constraints';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import useForm from 'dpl/shared/form/hooks/useForm';
import ErrorWrapper from 'dpl/form/components/ErrorWrapper';
import Button from 'dpl/common/design_system/Button';
import { pushParams } from 'dpl/actions/query_params';
import { SIGNUP_FORM_TYPES } from 'dpl/shared/constants/shared';
import { CONTACT_PATH } from 'dpl/shared/constants/urls';
import Input from 'dpl/common/design_system/Input';

import { forgotPasswordMutationDefinition } from './queryDefinitions';

interface ForgotPasswordFormV2Props {
  isCentered?: boolean;
}

export default function ForgotPasswordFormV2({
  isCentered = false
}: ForgotPasswordFormV2Props) {
  const [emailSent, setEmailSent] = useState<string | null>(null);
  const dispatch = useDispatch();

  const { mutateAsync: submitPasswordReset } = useResourceMutation(
    forgotPasswordMutationDefinition()
  );

  const {
    formState,
    handleFieldChange,
    handleSubmission,
    formErrors,
    isSubmitting,
    didSubmissionFail,
    didSubmissionSucceed
  } = useForm({
    defaultFormState: {
      email: ''
    },
    mapFormStateToValidationSchema: () => ({
      email: CONSTRAINT_TYPES.EMAIL
    }),
    onSubmit: (formData: any) =>
      submitPasswordReset({
        body: { user: formData }
      }).then(() => {
        setEmailSent(formData.email);
      })
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    handleSubmission();
  }

  const isSubmitDisabled =
    isSubmitting || !isEmpty(formErrors) || !formState.email?.trim();

  return (
    <div className="ForgotPasswordFormV2">
      {didSubmissionSucceed ? (
        <>
          <div className="ForgotPasswordFormV2__success">
            <h5 className={classnames('mb6 font-32', { tc: isCentered })}>
              Check your email
            </h5>
            <p
              className={classnames('secondary mb4 font-16', {
                tc: isCentered
              })}
            >
              We&apos;ve sent a reset link to {emailSent}. Don&apos;t see it?
              Check your spam or junk folder.
            </p>
            <div className={classnames('mb10', { tc: isCentered })}>
              <Button
                type="button"
                className="w-100"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  dispatch(
                    pushParams(
                      {
                        direct_signup: SIGNUP_FORM_TYPES.LOGIN
                      },
                      { hash: true }
                    )
                  );
                }}
              >
                Back to login
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          <form onSubmit={handleSubmit} noValidate>
            <div className="ForgotPasswordFormV2__form">
              <h5
                className={classnames('mb2 lh-title font-32', {
                  tc: isCentered
                })}
              >
                Forgot your password
              </h5>
              <p
                className={classnames('secondary font-16 pb6', {
                  tc: isCentered
                })}
              >
                Enter your email to receive a password reset link.
              </p>

              <div className="row mb3">
                <div className="col">
                  <label htmlFor="forgot-password-email">
                    <p className="secondary font-12 mb2">Email</p>
                    <ErrorWrapper
                      errors={formErrors.email}
                      isShown={didSubmissionFail}
                    >
                      <Input
                        id="forgot-password-email"
                        name="email"
                        type="email"
                        className="w-100"
                        value={formState.email || ''}
                        onChange={handleFieldChange}
                        placeholder="jane@example.com"
                        autoComplete="email"
                        required
                        aria-describedby={
                          formErrors.email
                            ? 'forgot-password-email-error'
                            : undefined
                        }
                      />
                    </ErrorWrapper>
                  </label>
                </div>
              </div>

              <div className="mb10">
                <button
                  data-test-id="forgot_password_submit"
                  disabled={isSubmitDisabled}
                  type="submit"
                  className={classnames('w-100 button button--primary pv3', {
                    'button--loading': isSubmitting,
                    'o-50 pointer-events-none': isSubmitDisabled
                  })}
                  aria-describedby={
                    isSubmitDisabled
                      ? 'forgot-password-submit-disabled'
                      : undefined
                  }
                >
                  {isSubmitting ? 'Sending...' : 'Send reset link'}
                </button>
              </div>
            </div>
          </form>
        </>
      )}
      <p className="secondary tc font-16">
        Need help?{' '}
        <a
          target="_blank"
          className="underline stone-700"
          href={CONTACT_PATH}
          rel="noreferrer"
        >
          Contact support
        </a>
      </p>
    </div>
  );
}
